import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dropdown } from 'semantic-ui-react';
import { IAppState, IPolicy } from 'types';
import Button from 'components/button';
import PoliciesList from 'components/policy-list';
import {
  checkPolicyIsUnfinished,
  getNonMSPolicies,
  getAdviser,
  getPolicyDetailsRoute,
  isFamilyOwner,
  PAGE_VIEW_EVENTS,
  setDashOverviewCollapse,
  getDashOverviewCollapse,
} from 'helpers';
import history from 'helpers/history';
import { POLICIES_SORTING_OPTIONS } from 'constants/options';
import {
  sortPolicies,
} from './util';
import {
  DeletePolicy,
  UpdatePolicy,
  ResetPolicyForm,
  GetPolicyScanByUserId,
  SelectPolicy,
  DeleteCreditCardPolicy,
} from 'actions';
import styles from './styles/policies.module.scss';
import { ADD_POLICY_ROUTE, MARKET_SCAN_ROUTE } from 'constants/routes';
import CreditCardList from './CreditCardList';
import DashboardOverview from 'components/dashboard-overview';
import { Plus, ChevronDown, ChevronUp } from 'react-feather';
import { PolicyStatus } from 'constants/policy';
import { MarketScanIcon } from 'components/svgs/MarketScanIcon';
import Q4EPopup from 'components/policy-wizard/Q4EPopup';
import useTracking from 'hooks/useTracking';
import DashboardPopup from './components/DashboardPopup';
interface IDispatchProps {
  deletePolicy(id: string): void;
  deleteCreditCard(id: string, sk: string): void;
  getPolicyScanByUserId(userId: string): void;
  updatePolicy(policy: IPolicy): void;
  resetPolicyForm(): void;
  selectPolicy(policy: IPolicy): void;
  onPolicyClick?(policy: IPolicy): void;
}

interface IDashboardProps extends IAppState, IDispatchProps {
  ownerName?: string;
}

const placeholderCard = (
  <Link to={ADD_POLICY_ROUTE}>
    <div className={styles.avatar} />
    <b>Add your first policy</b>
    <Plus className={styles.plus} />
  </Link>
);

const PoliciesContainer = ({
  policies: {
    bundles,
    policies: allPolicies,
    loading,
    selectedPolicy,
    policyScans,
    submitted,
  },
  creditCard,
  ownerName,
  referenceData: { employers, creditCards: ccReference, policyProviders },
  user,
  deletePolicy,
  deleteCreditCard,
  resetPolicyForm,
  selectPolicy,
  onPolicyClick,
  updatePolicy,
}: IDashboardProps) => {
  useTracking(PAGE_VIEW_EVENTS.Dashboard);

  useEffect(() => {
    if (selectedPolicy && submitted) {
      resetPolicyForm();
    }
  }, []);

  const [sort, setSort] = useState(POLICIES_SORTING_OPTIONS[0].value);
  const [overViewCollapse, setOverViewCollapse] = useState(
    !getDashOverviewCollapse(),
  );

  const selectCreditCard = (ccPolicy) => {
    if (onPolicyClick) {
      onPolicyClick(ccPolicy);
    } else {
      history.push(getPolicyDetailsRoute(ccPolicy.id));
    }
  };

  const adviser = getAdviser(user.advisers, user.createdBy);
  const handleSortChange = useCallback((_, { value }) => setSort(value), []);
  const policies = useMemo(() => getNonMSPolicies(allPolicies), [allPolicies]);

  const [policyList, q4ePolicies] = useMemo(() => {
    const sorted = sortPolicies(policies, sort);
    const { normal = [], q4e = [] } = R.groupBy(
      (p) => (p.employerId ? 'q4e' : 'normal'),
      sorted,
    );
    return [[...bundles, ...normal], q4e];
  }, [policies, bundles, sort]);

  const isOwnerOfCurrentFamily = useMemo(() => isFamilyOwner(user), [user]);

  const placeholderProps = isOwnerOfCurrentFamily
    ? {
        placeholder: placeholderCard,
        placeholderClassName: styles.placeholder,
      }
    : {};

  const [popupPolicy, setPopupOpen] = useState(null);

  const closePopup = () => setPopupOpen(null);


  const handlePolicyClick = (policy: IPolicy) => {
    if (onPolicyClick) {
      onPolicyClick(policy);
    } else {
      if (policy.employerId && policy.status === PolicyStatus.FINISH_SETUP) {
        const employer = employers.find(R.propEq('id', policy.employerId));
        const employerPolicy = employer.policies?.find(
          (p) => p.policyTypeId === policy.policyTypeId && p.showPopup,
        );
        if (employerPolicy) {
          return setPopupOpen({ employer, employerPolicy, policy });
        }
      }

      if (checkPolicyIsUnfinished(policy)) {
        // Need to put selected policy in the state so the
        // ADD_POLICY_ROUTE knows which policy to update on save
        selectPolicy(policy);
        history.push(ADD_POLICY_ROUTE);
      } else {
        history.push(getPolicyDetailsRoute(policy.id));
      }
    }
  };

  const handleOverViewCollapseClick = () => {
    setOverViewCollapse((value) => !value);
    setDashOverviewCollapse(overViewCollapse);
  };

  return (
    <div className={styles.dashboardList}>
      <div className={styles.titleSection}>
        <div className={styles.pageTitle}>
          My Dashboard
          <span></span>
        </div>
      </div>
      {user.showDashboardWalkthrough && <DashboardPopup />}
      <Q4EPopup
        {...popupPolicy}
        open={!!popupPolicy}
        onClose={closePopup}
        updatePolicy={updatePolicy}
      />
      <div className={styles.overview}>
        <div
          className={styles.overviewHeaderContainer}
          onClick={handleOverViewCollapseClick}
        >
          <div className={styles.overviewHeader}>Overview</div>
          {overViewCollapse ? (
            <ChevronUp className={styles.overviewCollapseBtn} />
          ) : (
            <ChevronDown className={styles.overviewCollapseBtn} />
          )}
        </div>
        <div className={styles.overviewContent}>
          <DashboardOverview
            policies={policies}
            adviser={adviser}
            collapse={overViewCollapse}
          />
        </div>
      </div>
      <div className={styles.policiesContainer}>
        <div className={styles.policiesHeader}>
          <div className={styles.title}>{`${ownerName} policies`}</div>
          <div className={styles.listMenu}>
            <div className={styles.sort}>
              <div className={styles.text}>
                Sort by: <div className={styles.value}>{sort}</div>
              </div>
              <Dropdown
                id="Dashboard_Sort"
                onChange={handleSortChange}
                className={`button icon ${styles.dropdown}`}
                floating={true}
                options={POLICIES_SORTING_OPTIONS}
                trigger={<></>}
                defaultValue={POLICIES_SORTING_OPTIONS[0].value}
              />
            </div>
            {isOwnerOfCurrentFamily && (
              <div className={styles.policiesBtns}>
                <Button
                  id="Dashboard_MarketScanBtn"
                  className={styles.marketScanBtn}
                  link={MARKET_SCAN_ROUTE}
                  labelPosition="left"
                  text={
                    <>
                      <MarketScanIcon />
                      Market Scan
                    </>
                  }
                  eventName="Web-App-Market-Scan-Clicked"
                />
                <Button
                  id="Dashboard_AddBtn"
                  link="/new"
                  text={
                    <>
                      <Plus size={20} />
                      Add policy
                    </>
                  }
                  className={styles.policyBtn}
                  labelPosition="left"
                />
              </div>
            )}
          </div>
        </div>
        <PoliciesList
          id="MyPolicies"
          policyProviders={policyProviders}
          policies={policyList}
          policyScans={policyScans}
          user={user}
          onClick={handlePolicyClick}
          loading={loading}
          deletePolicy={deletePolicy}
          isOwner={isOwnerOfCurrentFamily}
          showUpdateNow={isOwnerOfCurrentFamily}
          {...placeholderProps}
        />
      </div>
      {q4ePolicies.length > 0 && (
        <div className={styles.q4eContainer}>
          <div className={styles.title}>My workplace</div>
          <PoliciesList
            id="WorkplacePolicies"
            employers={employers}
            policyProviders={policyProviders}
            policies={q4ePolicies}
            user={user}
            onClick={handlePolicyClick}
            loading={loading}
            deletePolicy={deletePolicy}
            isOwner={isOwnerOfCurrentFamily}
            showContent={true}
            headers={[
              '',
              'Type/name',
              'Provider',
              'Benefits',
              'Excess',
              'Status',
              '',
            ]}
            headerClassName={styles.q4eHeader}
          />
        </div>
      )}
      <CreditCardList
        creditCard={creditCard}
        isOwner={isOwnerOfCurrentFamily}
        reference={ccReference}
        selectPolicy={selectCreditCard}
        deletePolicy={deleteCreditCard}
      />
    </div>
  );
};

const mapStateToProps = (state: IAppState) => {
  return {
    ...state,
  };
};

const mapDispatchToProps = {
  deletePolicy: DeletePolicy,
  deleteCreditCard: DeleteCreditCardPolicy,
  getPolicyScanByUserId: GetPolicyScanByUserId,
  updatePolicy: UpdatePolicy,
  resetPolicyForm: ResetPolicyForm,
  selectPolicy: SelectPolicy,
};

PoliciesContainer.defaultProps = {
  ownerName: 'My',
};

export default connect<IAppState, IDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(PoliciesContainer);
